import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { SITE } from "@/lib/site";
import { articles } from "@/mocks/articles";

const hotTitles = articles.filter((item) => item.hot).map((item) => item.title);

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) {
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50">
      <div className="hidden w-full items-center gap-4 border-b border-primary-700/40 bg-primary-700 px-4 py-2 text-background-50 md:flex md:px-8">
        <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-accent-100">
          <span className="flex h-4 w-4 items-center justify-center"><i className="ri-fire-fill text-sm text-accent-300"></i></span>
          今日更新 {articles.length} 条
        </span>
        <div className="relative flex-1 overflow-hidden">
          <div className="flex w-max animate-marquee items-center gap-10 whitespace-nowrap text-xs text-background-50/90">
            {[...hotTitles, ...hotTitles].map((title, index) => (
              <span key={`${title}-${index}`} className="flex items-center gap-2">
                <i className="ri-flashlight-fill text-accent-300"></i>
                {title}
              </span>
            ))}
          </div>
        </div>
        <Link to="/about" className="shrink-0 text-xs text-background-50/80 transition-colors hover:text-background-50">
          关于我们
        </Link>
      </div>

      <div className="w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:px-8">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-fire-fill text-xl"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-2xl tracking-wide text-foreground-950">爆料专区</span>
              <span className="mt-0.5 hidden text-[10px] tracking-[0.2em] text-foreground-500 sm:block">
                BAOLIAO ZHUANQU
              </span>
            </span>
          </Link>

          <nav aria-label="主导航" className="hidden items-center gap-1 lg:flex">
            {SITE.nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary-100 font-medium text-primary-700"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            <form onSubmit={submitSearch} className="relative" role="search">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索爆料关键词"
                className="h-9 w-40 rounded-md border border-background-200 bg-background-100 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-all placeholder:text-foreground-400 focus:w-52 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </form>
          </div>

          <button
            type="button"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-800 lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-3 lg:hidden">
            <form onSubmit={submitSearch} className="relative mb-3" role="search">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索爆料关键词"
                className="h-10 w-full rounded-md border border-background-200 bg-background-100 pl-9 pr-3 text-sm outline-none focus:border-primary-400"
              />
            </form>
            <div className="grid grid-cols-2 gap-2">
              {SITE.nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2.5 text-sm ${
                      isActive ? "bg-primary-100 font-medium text-primary-700" : "bg-background-100 text-foreground-700"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}